import { Slider } from '@miblanchard/react-native-slider';
import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { Dimensions, View } from 'react-native';
import { Text } from '@/components/Text';
import { styles } from './styles';
import { PressableNeolexon } from '@/components/PressableNeolexon';
const WARNING_STATE_KEYS = ['Verstehen', 'Lesen'];

const SCREEN_WIDTH = Dimensions.get('window').height;

type WritingState = Record<string, string | number | undefined>;

type VariableTitleEntry = {
  title: string;
  data: number[];
  globalStateName: string;
};

type RadioOptionItem = {
  label: string | number;
  value: string | number;
};

type RadioSubTitleItem = {
  title: string;
  globalStateName: string;
  isNotRadio?: false;
  data: RadioOptionItem[];
  sliderLabel?: never;
};

type SliderSubTitleItem = {
  title: string;
  globalStateName: string;
  isNotRadio: true;
  data: [number, number];
  sliderLabel: string;
};

type SubTitleItem = RadioSubTitleItem | SliderSubTitleItem;

type ExerciseUI = {
  title: string;
  subTitles: SubTitleItem[];
  variableTitles?: Record<string, VariableTitleEntry>;
};

export type WritingHandle = {
  getState: () => {
    state: WritingState;
    stateKey: string;
    outerValue: string | undefined;
    outerName: string | undefined;
  };
};

type WritingProps = {
  stateKey: string;
  UI: ExerciseUI;
  defaultValues?: Record<string, unknown>;
  outerValue?: string;
  outerValueName?: string;
  changeWarning: (index: number, type: 'add' | null) => void;
  warningIndex: number | undefined;
  setType?: string;
};

export const Writing = forwardRef<WritingHandle, WritingProps>(
  (
    {
      stateKey,
      UI,
      defaultValues,
      outerValue,
      outerValueName,
      changeWarning,
      warningIndex,
    },
    ref,
  ) => {
    const { title, subTitles, variableTitles } = UI;
    const hasVariableTitles = variableTitles != undefined;

    const [state, setState] = useState<WritingState>({
      ...(defaultValues as WritingState),
    });
    const [outerState, setOuterState] = useState<
      Record<string, string | undefined>
    >({
      [outerValueName ?? '']: outerValue,
    });

    const handleStateChange = (name: string, value: string | number) => {
      return () => {
        setState((prevState) => ({ ...prevState, [name]: value }));
      };
    };

    const handleOuterStateChange = (name: string, value: string) => {
      return () => {
        setOuterState((prevState) => ({ ...prevState, [name]: value }));
      };
    };

    const drawVariableTitles = (variableData: VariableTitleEntry) => {
      return (
        <View>
          <Text style={styles.subTitle}>{variableData.title}</Text>
          <View style={styles.row}>
            {variableData.data.map((value) => {
              const isActive = value === state.value;
              return (
                <PressableNeolexon
                  style={
                    isActive ? [styles.radio, styles.radioActive] : styles.radio
                  }
                  key={value}
                  onPress={handleStateChange(
                    variableData.globalStateName,
                    value,
                  )}
                >
                  <Text
                    style={
                      isActive
                        ? [styles.radioText, styles.radioActiveText]
                        : styles.radioText
                    }
                  >
                    {value}
                  </Text>
                </PressableNeolexon>
              );
            })}
          </View>
        </View>
      );
    };

    const getState = (): ReturnType<WritingHandle['getState']> => ({
      state,
      stateKey,
      outerValue: outerState[outerValueName ?? ''],
      outerName: outerValueName,
    });

    useImperativeHandle(ref, () => ({ getState }), [state, outerState]);

    useEffect(() => {
      if (WARNING_STATE_KEYS.includes(stateKey)) {
        if (state.setting === 'fromset') {
          changeWarning(warningIndex!, 'add');
        } else {
          changeWarning(warningIndex!, null);
        }
      }
    }, [state.setting]);

    return (
      <>
        <Text style={styles.title}>{title}</Text>
        {subTitles.map((subTitle) => {
          return (
            <View key={subTitle.title}>
              <Text style={styles.subTitle}>{subTitle.title}</Text>
              <View style={styles.row}>
                {!subTitle.isNotRadio ? (
                  subTitle.data.map((eachData) => {
                    const hasOuterState =
                      subTitle.globalStateName == 'audioSpeedSetting';
                    let isActive: boolean;
                    if (hasOuterState) {
                      isActive =
                        eachData.value === outerState[outerValueName ?? ''];
                    } else {
                      isActive =
                        eachData.value === state[subTitle.globalStateName];
                    }
                    return (
                      <PressableNeolexon
                        style={
                          isActive
                            ? [styles.radio, styles.radioActive]
                            : styles.radio
                        }
                        onPress={
                          hasOuterState
                            ? handleOuterStateChange(
                                outerValueName ?? '',
                                String(eachData.value),
                              )
                            : handleStateChange(
                                subTitle.globalStateName,
                                eachData.value,
                              )
                        }
                        key={eachData.value}
                      >
                        <Text
                          style={
                            isActive
                              ? [styles.radioText, styles.radioActiveText]
                              : styles.radioText
                          }
                        >
                          {eachData.label}
                        </Text>
                      </PressableNeolexon>
                    );
                  })
                ) : (
                  <View
                    style={[
                      styles.row,
                      { width: '100%', alignItems: 'center' },
                    ]}
                  >
                    <Text
                      style={[
                        styles.subTitle,
                        state.setting === 'norecord' && { color: '#aaaa' },
                      ]}
                    >
                      {subTitle.sliderLabel}
                    </Text>
                    <Slider
                      value={state.value as number | undefined}
                      containerStyle={{
                        width: SCREEN_WIDTH * 0.6,
                        height: 50,
                        marginHorizontal: 10,
                      }}
                      step={1}
                      minimumValue={subTitle.data[0]}
                      maximumValue={subTitle.data[1]}
                      onValueChange={(newValue) => {
                        handleStateChange(
                          subTitle.globalStateName,
                          (newValue as number[])?.[0] ?? subTitle.data[0],
                        )();
                      }}
                      disabled={state.setting === 'norecord'}
                      thumbTintColor={
                        state.setting === 'norecord' ? '#aaaa' : '#50BF92'
                      }
                      minimumTrackTintColor={
                        state.setting === 'norecord' ? '#aaaa' : '#50BF92'
                      }
                    />
                    <Text
                      style={[
                        styles.subTitle,
                        state.setting === 'norecord' && { color: '#aaaa' },
                      ]}
                    >
                      {state.value}
                    </Text>
                  </View>
                )}
              </View>
            </View>
          );
        })}
        {hasVariableTitles && state.setting
          ? drawVariableTitles(variableTitles![state.setting as string])
          : null}
      </>
    );
  },
);
Writing.displayName = 'Writing';
